'use client';

import React, { useState } from 'react';
import { X, CheckCircle, Loader2, Lock, AlertCircle, PhoneCall } from 'lucide-react';
import { trackLead } from '../lib/analytics';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
}

const BookingModal: React.FC<BookingModalProps> = ({ isOpen, onClose, title = 'Bezpłatna kwalifikacja 30 min', description = 'Sprawdzimy razem czy AI caller ma sens w Twoim procesie sprzedaży' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const API_KEY = "eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJhdWQiOiI0IiwianRpIjoiMjBkMzM2ZjJiYTY4MjA2NTk5MjQ5MmNlZmRiZWZmNzBiMjViYTNlNGUxOWU1ZWM5ZDQzZTYxYzFlZDVmZDU5YTYyZWZlOGVkODI5ZTllYmEiLCJpYXQiOjE3Njg1OTU1NjcuNDk3MzMxLCJuYmYiOjE3Njg1OTU1NjcuNDk3MzM0LCJleHAiOjQ5MjQyNjkxNjcuNDg3OTYsInN1YiI6IjE0NjUyMDUiLCJzY29wZXMiOltdfQ.OoIKCF2o1Ovh7dyogOUAbLAxmqHxodtIrdxTOXYJK0710cq4h4mxYpK9W2yBqZOZGo4T9iMK6munJQ-523YZApVBzioaB_x-xe8BVnerTq2qzpFOSsWS5ZOdRRXenkushX75ETSkAWZOeRkjy0UCK2HacsdMVrKZFbRrBrsi2ljM-Zm2ozus8lb1SgvGs82GalShM9_A04s6hn08SpnEGwGrwfrpSlzuKyTG7yiAGUcfLqcUQkYTF2Uf2nAjBgi2ra9A382hPCYybvOWIzUrZhVklTiiGQGbX05H1SgpMmnMzxPpSF8Ffdel47XfHK3R_mVLGtvVS2yNgzlbRg_0ZoGuDwMnHKldzw6hYi4bThM3zRtO3bVom3FXwpHn0f8oBdfVEdphxyxYUEIyNzcNnaHkDQHFy4dnbGUnehnoKv-fG5paPo8Y11zOLQdH0Uc-5XGFfpAjqwsRVZSMQDyO_DVhcWsUb5_hc9AULusjc_ivLuI1r8lR8azSQjy-OpHz4PSUgHRzAeT_2w29L4weg8a30xYUIw47gKg3-qQG7eLgovzhqkw6bUkr5Ngbc37g873NKmiBZ-mjFk2Fk73nyKzdFn3b6hYJEhgJZy8bd8ZZLu91OHbh2yq0jTOy6BOk01MvntuCz49HQAhgl4d8tHjHm6q6gkDlw-DAL5besKw";
    const GROUP_ID = "176785313373357906";

    try {
      const response = await fetch('https://connect.mailerlite.com/api/subscribers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          fields: {
            name: formData.name,
            phone: formData.phone,
          },
          groups: [GROUP_ID],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let msg = 'Wystąpił błąd podczas zapisywania.';
        if (errorData && errorData.message) msg = errorData.message;
        throw new Error(msg);
      }

      setStatus('success');
      setFormData({ name: '', email: '', phone: '' });
      trackLead();

      const url = new URL(window.location.href);
      url.searchParams.set('lead', 'success');
      window.history.replaceState({}, '', url.toString());

    } catch (error: any) {
      console.error('Form Submission Error:', error);
      setErrorMessage(error.message || 'Wystąpił nieoczekiwany błąd. Sprawdź połączenie.');
      setStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100">

        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 bg-gray-50 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition z-20"
        >
          <X className="w-5 h-5" />
        </button>

        {status === 'success' ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 ring-4 ring-green-50">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Gotowe!</h3>
            <p className="text-gray-600 mb-4 max-w-sm mx-auto leading-relaxed">
              Wniosek przyjęty. Zadzwonimy do Ciebie w ciągu <strong>2 godzin roboczych</strong>, żeby umówić kwalifikację.
            </p>
            <p className="text-sm text-gray-400 mb-8">
              Sprawdź telefon — zadzwonimy z nieznanego numeru. Odbierz — to my.
            </p>
            <button
              onClick={onClose}
              className="w-full py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold rounded-xl transition"
            >
              Wróć do strony
            </button>
          </div>
        ) : (
          <div>
            {/* Header */}
            <div className="bg-slate-950 px-8 pt-10 pb-6 text-center">
              <div className="w-14 h-14 bg-cyan-400/10 border border-cyan-400/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <PhoneCall className="w-7 h-7 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1">{title}</h3>
              <p className="text-slate-400 text-sm">
                {description}
              </p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-400/30 px-3 py-1 text-xs font-bold text-orange-300">
                ⏳ Zostały 2 miejsca w marcu
              </div>
            </div>

            <div className="p-8">
              {status === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-red-800">Wystąpił problem</h4>
                    <p className="text-xs text-red-600 mt-1">{errorMessage}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700">Imię i Nazwisko</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-cyan-500/25 focus:border-cyan-500 transition outline-none"
                    placeholder="Np. Michał Kowalski"
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700">Numer Telefonu</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-cyan-500/25 focus:border-cyan-500 transition outline-none"
                    placeholder="Np. 500 123 456"
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700">Adres Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-cyan-500/25 focus:border-cyan-500 transition outline-none"
                    placeholder="Np. michal@firma.pl"
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full mt-2 py-4 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-extrabold text-lg rounded-xl shadow-xl shadow-cyan-500/20 transition-all transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Wysyłam...
                    </>
                  ) : (
                    'Umów bezpłatną kwalifikację →'
                  )}
                </button>

                <div className="flex items-center justify-center gap-2 text-xs text-gray-400 mt-4 bg-gray-50 py-2 rounded-lg">
                  <Lock className="w-3 h-3" />
                  <span>Twoje dane są bezpieczne. Oddzwonimy w ciągu 2h roboczych.</span>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
